import * as THREE from 'three';
import { doorZones } from './doorZones';

// WALLS ARE SOLID (2026-11-01) — the rule SolidClamp applies to every step
// indoors (its header has the why). Pure three.js, so it can be run against a
// real room model outside the browser.
//
// One ray along the step at torso height; the first near-vertical, visible,
// depth-writing surface that is not in or beside a door stops you WALL_R short
// of it, and what is left of the step slides you along it (once more checked).
export const WALL_H = 0.5;     // the ray's height over your feet (≈1.1m): above any stair, table or step
export const WALL_R = 0.13;    // stop this far short of a wall (≈29cm: a shoulder)
const WALL_NY = 0.35;          // a surface is a wall when its normal is this close to level
const TELEPORT = 0.8;          // a frame's move longer than this was not a step (spawn, stand-up, a ride)

const ray = new THREE.Raycaster();
const o = new THREE.Vector3();
const d = new THREE.Vector3();
const n = new THREE.Vector3();

// Light, not matter: a shaft, a glow, haze — by name, for models that do not
// blend them additively.
const NOT_SOLID = /beam|shaft|godray|glow|haze|fog|dust|volumetric|light_?cone/i;

/**
 * Seen, and something you would walk into: every ancestor visible, and not
 * light. NOT "writes depth" — GLTFLoader turns depth writing off for every
 * blended material, so that test let you walk out through a window's glass
 * (2026-11-01, measured against the classroom). Glass is a wall; an additive
 * glow is not.
 */
function isWallMesh(obj) {
  for (let p = obj; p; p = p.parent) if (p.visible === false) return false;
  if (obj.userData?.passThrough || NOT_SOLID.test(obj.name || '')) return false;
  const m = Array.isArray(obj.material) ? obj.material[0] : obj.material;
  return !!m && m.blending !== THREE.AdditiveBlending;
}

/** The first wall from (x, y, z) along `dir` (unit, level) within `far`: { dist, nx, nz } (its level normal), or null. */
export function wallAlong(model, x, y, z, dir, far) {
  o.set(x, y, z);
  ray.set(o, dir);
  ray.far = far;
  ray.firstHitOnly = false;
  for (const h of ray.intersectObject(model, true)) {
    if (!h.face || !isWallMesh(h.object)) continue;
    n.copy(h.face.normal).transformDirection(h.object.matrixWorld);
    if (Math.abs(n.y) > WALL_NY) continue;                                // a floor, a step, a sloped top
    if (doorZones.near?.(h.point.x, h.point.y, h.point.z)) continue;      // a door: walk into it
    const len = Math.hypot(n.x, n.z) || 1;
    return { dist: h.distance, nx: n.x / len, nz: n.z / len };
  }
  return null;
}

/**
 * The step from `prev` to `P` (world; P is moved in place), stopped short of
 * walls and slid along them. `feetY` = the walker's feet. A step too long to be
 * a step (a teleport) is left alone. Returns true when a wall changed it.
 */
export function stopAtWalls(model, prev, P, feetY) {
  const dx = P.x - prev.x; const dz = P.z - prev.z;
  const len = Math.hypot(dx, dz);
  if (len < 1e-5 || len > TELEPORT) return false;
  const y = feetY + WALL_H;
  d.set(dx / len, 0, dz / len);
  const hit = wallAlong(model, prev.x, y, prev.z, d, len + WALL_R);
  if (!hit) return false;
  const go = Math.max(0, hit.dist - WALL_R);
  if (go >= len) return false;
  let x = prev.x + d.x * go; let z = prev.z + d.z * go;
  // What is left of the step, along the wall.
  const left = len - go;
  const dot = d.x * hit.nx + d.z * hit.nz;
  const sx = (d.x - hit.nx * dot) * left; const sz = (d.z - hit.nz * dot) * left;
  const sl = Math.hypot(sx, sz);
  if (sl > 1e-5) {
    d.set(sx / sl, 0, sz / sl);
    const hit2 = wallAlong(model, x, y, z, d, sl + WALL_R);
    const s2 = hit2 ? Math.max(0, Math.min(sl, hit2.dist - WALL_R)) : sl;
    x += d.x * s2; z += d.z * s2;
  }
  P.x = x; P.z = z;
  return true;
}
